import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import "./banner.scss";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import lottie from "lottie-web";
import { Link } from "react-router-dom";

gsap.registerPlugin(ScrollTrigger);

const Banner = () => {
  const headRef = useRef(null);
  const paraRef = useRef(null);
  const buttonRef = useRef(null);
  const textRef = useRef(null);
  const leftIconRef = useRef(null);
  const rightIconRef = useRef(null);
  const bgRef = useRef(null);
  const iconsRef = useRef(null);
  const bannerRef = useRef(null);
  const leftCardRef = useRef(null);
  const rightCardRef = useRef(null);
  const videoCardRef = useRef(null);
  const innerBannerRef = useRef(null);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  useEffect(() => {
    gsap.to(innerBannerRef.current, {
      opacity: 0,
      scale: 0.9,
      height: 0,
      ease: "power2.out",
      scrollTrigger: {
        trigger: innerBannerRef.current,
        start: "top 20%",
        end: "bottom 80%",
        scrub: true,
      },
    });
  }, []);

  const updateStyles = () => {
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    setViewportHeight(vh);

    if (bannerRef.current) {
      bannerRef.current.style.marginTop = `${vh * 0.3}px`; // 30vh
      bannerRef.current.style.paddingTop = `${vw * 0.06}px`; // 13.472vw
    }
  };

  useEffect(() => {
    updateStyles(); // Set initial values

    // Listen for window resize and update values dynamically
    window.addEventListener("resize", updateStyles);

    let ctx = gsap.context(() => {
      let tl = gsap.timeline({
        scrollTrigger: {
          trigger: bannerRef.current,
          start: "top+=0px top",
          end: "+=8000px",
          scrub: true,
          pin: true,
          anticipatePin: 1,
          toggleActions: "play none none reset",
          // markers: true,
        },
      });

      tl.fromTo(
        leftCardRef.current,
        { y: 400, opacity: 0 },
        { y: 0, opacity: 1, duration: 10, ease: "power2.out" }
      )
        .fromTo(
          rightCardRef.current,
          { y: 400, opacity: 0 },
          { y: 0, opacity: 1, duration: 10, ease: "power2.out" },
          "-=1"
        )
        .fromTo(
          videoCardRef.current,
          { opacity: 0 },
          { opacity: 1, duration: 10, ease: "power2.out" },
          "-=2"
        );
    });

    return () => {
      ctx.revert(); // Cleanup GSAP animations
      window.removeEventListener("resize", updateStyles); // Remove event listener
    };
  }, []);

  useEffect(() => {
    const button = buttonRef.current;
    const text = textRef.current;
    const leftIcon = leftIconRef.current;
    const rightIcon = rightIconRef.current;
    const icons = iconsRef.current;

    gsap.fromTo(
      button,
      { scaleX: 0, opacity: 0 },
      { scaleX: 1, opacity: 1, duration: 0.6, ease: "power2.out", delay: 0.4 }
    );

    gsap.fromTo(
      [leftIcon, rightIcon],
      { y: 20, opacity: 0, immediateRender: false },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        ease: "power2.out",
        delay: 0.7,
        stagger: 0.1,
      }
    );

    ScrollTrigger.create({
      trigger: document.body,
      start: "top+=100",
      end: "bottom-=100",
      onUpdate: (self) => {
        if (self.direction === 1) {
          button.addEventListener("mouseenter", () => {
            gsap.to(button, {
              width: "13.5vw",
              duration: 0.5,
              gap: "0.2776vw",
              ease: "power2.out",
            });
            gsap.to(text, {
              opacity: 1,
              width: "6.7216vw",
              y: 0,
              duration: 0.3,
              ease: "power2.out",
            });
            gsap.to(icons, { left: "0", duration: 0.5, ease: "power2.out" });
          });

          button.addEventListener("mouseleave", () => {
            gsap.to(button, {
              width: "6vw",
              gap: "0",
              duration: 0.5,
              ease: "power2.out",
            });
            gsap.to(text, {
              opacity: 0,
              width: 0,
              y: -10,
              duration: 0.3,
              ease: "power2.out",
            });
            gsap.to(icons, { left: "36px", duration: 0.5, ease: "power2.out" });
          });
          gsap.to(icons, {
            left: "36px",
            duration: 0.5,
            ease: "power2.out",
          });
          gsap.to(button, {
            bottom: "50px",
            width: "6vw",
            gap: "0",
            duration: 0.5,
            ease: "power2.out",
          });
          gsap.to([text], {
            opacity: 0,
            width: 0,
            y: -10,
            duration: 0.3,
            ease: "power2.out",
          });
        } else {
          button.addEventListener("mouseenter", () => {
            gsap.to(button, {
              width: "13.5vw",
              duration: 0.5,
              gap: "0.2776vw",
              ease: "power2.out",
            });
            gsap.to(text, {
              opacity: 1,
              width: "6.7216vw",
              y: 0,
              duration: 0.3,
              ease: "power2.out",
            });
            gsap.to(icons, { left: "0", duration: 0.5, ease: "power2.out" });
          });
          button.addEventListener("mouseleave", () => {
            gsap.to(button, {
              width: "13.5vw",
              gap: "0.2776vw",
              duration: 0.5,
              ease: "power2.out",
            });
            gsap.to(text, {
              opacity: 1,
              width: "6.7216vw",
              y: 0,
              duration: 0.3,
              ease: "power2.out",
            });
            gsap.to(icons, { left: "0", duration: 0.5, ease: "power2.out" });
          });
          gsap.to(icons, {
            left: "0",
            duration: 0.5,
            ease: "power2.out",
          });
          gsap.to(button, {
            bottom: "47vh",
            width: "13.5vw",
            gap: "0.2776vw",
            duration: 0.5,
            ease: "power2.out",
          });
          gsap.to([text], {
            opacity: 1,
            width: "6.7216vw",
            y: 0,
            duration: 0.3,
            ease: "power2.out",
          });
        }
      },
    });
  }, []);
  useEffect(() => {
    const tl = gsap.timeline();

    // Heading animation - Expands width from left
    gsap.set(headRef.current, {
      width: "0%",
      opacity: 1,
      overflow: "hidden",
      whiteSpace: "nowrap",
    });

    tl.to(headRef.current, {
      width: "100%", // Expanding from left to right
      duration: 1, // Adjust timing as needed
      ease: "power2.out",
    });

    // Paragraph animation - Comes from bottom with opacity fade-in
    tl.from(
      paraRef.current,
      {
        y: 30,
        opacity: 0,
        duration: 0.8,
        ease: "power2.out",
      },
      "-=0.5"
    ); // Slightly earlier
  }, []);

  const container1 = useRef(null);
  const container2 = useRef(null);

  useEffect(() => {
    const animation1 = lottie.loadAnimation({
      container: container1.current,
      renderer: "svg",
      loop: true,
      autoplay: true,
      path: "/assets/comment.json", // Replace with your JSON file path
    });

    const animation2 = lottie.loadAnimation({
      container: container2.current,
      renderer: "svg",
      loop: true,
      autoplay: true,
      path: "/assets/wallet.json",
    });

    return () => {
      animation1.destroy();
      animation2.destroy();
    };
  }, []);

  const [isModalOpen, setIsModalOpen] = useState(false);
  return (
    <>
      <section className="main-banner d-none-for-mobile">
        <img
          src="/assets/roadmapassets/banner/topbg.png"
          alt="mainbannertopbg"
          className="mainbannertopbg"
        />
        <img
          src="/assets/roadmapassets/banner/topbgmbl.png"
          alt="mainbannertopbg"
          className="mainbannertopbgmbl d-none"
        />
        <img
          src="/assets/roadmapassets/banner/bannershade.png"
          alt="bannershade"
          className="bannershade"
        />
        <img
          src="/assets/roadmapassets/banner/bannershadembl.png"
          alt="bannershade"
          className="bannershadembl d-none"
        />
        <div ref={innerBannerRef} className="innerbanner">
          <div className="d-flex justify-content-start align-items-start">
            <h1 className="innerbannerhead" ref={headRef}>
              Where Money Chats
            </h1>
          </div>

          <p className="innerbannerpara" ref={paraRef}>
            Be among the first to experience TOMI’s chat and wallet app – sign
            up now for early access and an exclusive airdrop!
          </p>
        </div>
        <div className="button-wrapper">
          <button
            ref={buttonRef}
            data-bs-toggle="modal"
            data-bs-target="#exampleModal"
            className="animated-btn"
          >
            {/* <div ref={iconsRef} className="iconsmain"> */}
            <span ref={leftIconRef} className="icon iconone">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="16"
                viewBox="0 0 15 16"
                fill="none"
              >
                <path
                  d="M7.49946 0.828125C3.53838 0.828125 0.327637 4.03886 0.327637 7.99995C0.327637 9.665 0.895043 11.198 1.84692 12.415C1.92714 12.5177 2.0729 12.5402 2.18345 12.4707L3.77512 11.4699C3.79175 11.4592 3.80643 11.4474 3.82012 11.4337L6.10932 9.99957V14.8225C6.10932 14.9468 6.19736 15.0554 6.32063 15.0749C6.70411 15.1385 7.09739 15.1718 7.49946 15.1718C7.90154 15.1718 8.32612 15.1366 8.72428 15.0671C8.84656 15.0456 8.93363 14.9389 8.93363 14.8147V10.027L11.2072 11.4513C11.214 11.4562 11.2199 11.4621 11.2267 11.467L12.8213 12.4629C12.9319 12.5324 13.0767 12.5089 13.1569 12.4062C13.603 11.8348 13.964 11.1941 14.2213 10.5034C14.2633 10.3899 14.2183 10.2637 14.1156 10.1991L13.7928 9.99664C13.783 9.98783 13.7722 9.97903 13.7605 9.9712L7.66284 6.15099C7.65207 6.14414 7.64034 6.13925 7.62957 6.13436C7.54838 6.08642 7.44468 6.08349 7.35859 6.13729L2.8164 8.98313C2.74694 8.65932 2.70977 8.32376 2.70977 7.97941C2.70977 5.33412 4.85417 3.18971 7.49946 3.18971C9.90507 3.18971 11.8959 4.96237 12.2373 7.27308C12.249 7.35135 12.2931 7.4208 12.3596 7.46287L14.2712 8.6603C14.4326 8.76204 14.6449 8.66029 14.6566 8.46953C14.6664 8.31398 14.6723 8.15648 14.6723 7.99897C14.6723 4.03886 11.4605 0.828125 7.49946 0.828125Z"
                  fill="#E8F2FF"
                />
              </svg>
            </span>
            {/* <span ref={rightIconRef} className="icon icontwo">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M13.0588 11.1833C12.8821 11.5714 12.6634 11.9411 12.4063 12.2867C12.1674 12.6414 11.888 12.9703 11.5734 13.2675C11.2832 13.5341 10.8993 13.69 10.4952 13.7054C10.1517 13.6905 9.81427 13.6147 9.50031 13.482C9.16207 13.3457 8.79902 13.2728 8.43138 13.2675C8.05258 13.2748 7.67876 13.3506 7.33005 13.4908C7.02968 13.6183 6.7092 13.6982 6.38143 13.7273C6.17474 13.724 5.97082 13.6816 5.78168 13.6027C5.59255 13.5237 5.42201 13.4097 5.28011 13.2675C4.95235 12.9663 4.6592 12.6331 4.40552 12.2735C4.0094 11.735 3.69768 11.1451 3.48004 10.5221C3.23009 9.85686 3.09873 9.15696 3.09134 8.45098C3.07127 7.75789 3.25382 7.07301 3.61886 6.47183C3.87689 6.02472 4.25524 5.6501 4.71556 5.38592C5.16646 5.1312 5.68049 4.99374 6.20559 4.98747C6.60194 5.01077 6.99138 5.09661 7.35782 5.24143C7.65268 5.3655 7.96404 5.45094 8.2833 5.49539C8.63037 5.42698 8.96973 5.32728 9.29671 5.19764C9.73678 5.02787 10.21 4.94876 10.6849 4.96557C11.1337 4.97753 11.5736 5.08679 11.9707 5.28492C12.3678 5.48305 12.7116 5.76477 12.9755 6.1084C12.5567 6.31755 12.2082 6.63348 11.9694 7.02041C11.7306 7.40734 11.611 7.84982 11.6243 8.29773C11.6196 8.64016 11.6918 8.97964 11.8362 9.29367C11.9806 9.60769 12.1938 9.8891 12.4619 10.1192C12.6981 10.3357 12.9721 10.512 13.2717 10.6403C13.2023 10.8242 13.1328 10.9994 13.0588 11.1701V11.1833ZM10.7266 2.49602C10.7182 3.08838 10.4831 3.65778 10.0649 4.0986C9.85324 4.37441 9.57458 4.59796 9.25241 4.75039C8.93025 4.90281 8.57404 4.97962 8.21389 4.97433C8.209 4.90289 8.209 4.83122 8.21389 4.75978C8.23366 4.15917 8.48176 3.58548 8.91263 3.14406C9.15232 2.88927 9.44337 2.68234 9.7687 2.53542C10.0729 2.38833 10.4046 2.29908 10.7451 2.27271C10.7496 2.34854 10.7496 2.42456 10.7451 2.50039L10.7266 2.49602Z"
                      fill="#E8F2FF"
                    />
                  </svg>
                </span> */}
            {/* </div> */}

            <span ref={textRef} className="button-text">
              Get Early Access
            </span>

            <div ref={bgRef} className="hover-bg"></div>
          </button>
        </div>
        <div ref={bannerRef} className="lowerbanner">
          <div ref={leftCardRef} className="lowercard mblorder-2">
            <div className="lowercardhead">
              <div ref={container1} className="svgwidthset" />
              <h2 className="cardhead">
                Free
                <span className="boldhead"> Yourself</span>
              </h2>
            </div>

            <p className="cardpara">
              from bouncing between apps - chat and send payments in one place.
            </p>
            <Link to="/chatandpay">
              <button className="cardbtn">Dive In</button>
            </Link>
          </div>
          <div className="bannervideo">
            <video
              className="lowerbannerimg mblorder-1"
              muted="muted"
              playsinline="playsinline"
              autoPlay
              loop
              width="100%"
              id="myVideo"
              style={{ zIndex: "99" }}
            >
              <source src="/assets/tomivideo.mp4" type="video/mp4" />
            </video>
            <video
              className="lowerbannerimg mblorder-1 secondvideo"
              muted="muted"
              playsinline="playsinline"
              autoPlay
              loop
              width="100%"
              id="myVideo"
              ref={videoCardRef}
            >
              <source src="/assets/wallet.mp4" type="video/mp4" />
            </video>
          </div>

          <div ref={rightCardRef} className="lowercard mblorder-3">
            <div className="lowercardhead">
              <div ref={container2} className="svgwidthset" />
              <h2 className="cardhead">
                Free your
                <span className="boldhead"> Money</span>
              </h2>
            </div>
            <p className="cardpara">
              with an integrated wallet. Now you can instantly view balances,
              access your wallet address, and send and receive tokens across
              multiple blockchains directly within your chats.
            </p>
            <Link to="/feature">
            <button className="cardbtn">Explore More Features</button>
            </Link>
          </div>
        </div>
      </section>

    </>
  );
};

export default Banner;
